import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { readStoredMode, writeStoredMode, type ViewMode } from '../lib/mode'

interface ModeContextValue {
  mode: ViewMode
  setMode: (m: ViewMode) => void
  isSimple: boolean
  isAdvanced: boolean
}

const ModeCtx = createContext<ModeContextValue | null>(null)

export function useViewMode(): ModeContextValue {
  const v = useContext(ModeCtx)
  if (!v) throw new Error('useViewMode must be used inside <ModeProvider>')
  return v
}

export function ModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ViewMode>('simple')
  const [needsChoice, setNeedsChoice] = useState<boolean>(false)

  useEffect(() => {
    const stored = readStoredMode()
    if (stored == null) {
      setNeedsChoice(true)
    } else {
      setModeState(stored)
    }
  }, [])

  const setMode = useCallback((m: ViewMode) => {
    setModeState(m)
    writeStoredMode(m)
    setNeedsChoice(false)
  }, [])

  const value: ModeContextValue = {
    mode,
    setMode,
    isSimple: mode === 'simple',
    isAdvanced: mode === 'advanced',
  }

  return (
    <ModeCtx.Provider value={value}>
      {children}
      {needsChoice && <WelcomeDialog onChoose={setMode} />}
    </ModeCtx.Provider>
  )
}

function WelcomeDialog({ onChoose }: { onChoose: (m: ViewMode) => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sezi-welcome-title"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div className="max-w-lg w-full bg-surface border border-border rounded-lg shadow-xl p-5 sm:p-6">
        <h2 id="sezi-welcome-title" className="text-text text-lg sm:text-xl font-semibold mb-2">
          Sezi'ye hoş geldiniz
        </h2>
        <p className="text-text-dim text-sm leading-relaxed mb-5">
          Bu uygulama, meme MR görüntülerini yapay zekayla incelemek için
          geliştirilmiş bir araştırma prototipidir. Başlamadan önce hangi
          görünümle ilerlemek istediğinizi seçer misiniz? Bu seçimi daha sonra
          üst menüden değiştirebilirsiniz.
        </p>
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => onChoose('simple')}
            className="w-full text-left border border-border hover:border-accent rounded p-3 sm:p-4 bg-surface-2 transition-colors"
          >
            <div className="text-text font-medium mb-1">Basit görünüm</div>
            <div className="text-text-dim text-xs sm:text-sm leading-relaxed">
              Tek bir tahmin ve güven oranı gösterilir. Teknik ayrıntılar ve
              ikinci modelin çıktısı gizlenir. Konuyla yeni tanışan ziyaretçiler
              için önerilir.
            </div>
          </button>
          <button
            type="button"
            onClick={() => onChoose('advanced')}
            className="w-full text-left border border-border hover:border-accent rounded p-3 sm:p-4 bg-surface-2 transition-colors"
          >
            <div className="text-text font-medium mb-1">İleri görünüm</div>
            <div className="text-text-dim text-xs sm:text-sm leading-relaxed">
              İki modelin tahminleri, fold olasılıkları, kohort etiketi ve
              teknik ayrıntılar dahil her şey görünür kalır. Konuya hâkim
              ziyaretçiler için.
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}
